import React, { useEffect, useState } from "react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import Paper from "@/components/paper/Paper";
import { DataMaskingComponent, DataMaskingCreditReportsComponent } from "@/components/dataMasking/MaskDynamicComponent";
import { BusinessDirectoryAccordionFragmentParameter } from "@/interfaces/CompanyProfile";
import { dnbComLog, LogSeverity, track_event } from "@/shared/FunctionCore";
import "@/app/pages/companyProfile/businessDirectoryAccordion/BusinessDirectoryAccordionStyles.scss";

interface AccordionListComponentProperties {
	"buyCreditReportsDirectoryPathRewritten": string;
	"description": string | null | undefined;
	"hooversFreeTrialDirectoryPathRewritten": string;
	"identifyingClassName": string | null | undefined;
	"index": number;
	"label": string | null | undefined;
	"title": string | null | undefined;
}

const AccordionListComponent = (props: AccordionListComponentProperties) => {
	const {
		buyCreditReportsDirectoryPathRewritten,
		description,
		hooversFreeTrialDirectoryPathRewritten,
		identifyingClassName,
		index,
		label,
		title
	} = props;

	const [activeIndex, setActiveIndex] = useState<number | undefined>();

	const sentinelValue = "?\t\t \t \t  \t\t  \t \t \t\t\t\t \t  \t\t\t   \t\t\t  \t\t  \t\t\t \t\t \t\t  \t\t \t\t\t\t   \t \t ";

	const dataMasked = description?.includes(sentinelValue) ?? false;

	const creditReportsSentinelValue = "#\t\t \t \t  \t\t  \t \t \t\t\t\t \t  \t\t\t   \t\t\t  \t\t  \t\t\t \t\t \t\t  \t\t \t\t\t\t   \t \t ";

	const creditReportsDataMasked = description?.includes(creditReportsSentinelValue) ?? false;

	const propertiesIndexIsActiveIndex = index === activeIndex;

	return <div
		className="accordion-items"
		data-allow-toggle="false"
		key={index}>
		<button
			aria-controls={identifyingClassName!}
			aria-expanded={propertiesIndexIsActiveIndex ? "true" : "false"}
			className={`accordion-trigger ${identifyingClassName}`}
			onClick={() =>
				setActiveIndex((bo) =>
					bo === index
						? void 0
						: index)}
			type="button"
		>
			<div className="faq-question">
				{title}
			</div>
		</button>

		<div
			aria-labelledby={label!}
			className={classnames("detail-faq", identifyingClassName, {
				"active": propertiesIndexIsActiveIndex,
			})}
		>
			{/* className={
				cx(propertiesIndexIsActiveIndex ? classCombiner(styles, "active") : void 0, classCombiner(styles, "detail"), identifyingClassName)}
			> */}
			{
				dataMasked
					?
					(() => {
						const split = description!.replace(`?${sentinelValue}`, sentinelValue).split(sentinelValue);
						const modifiedSplit1 = split[1]!.replace(/data-tippy-content="tippyContent"/g, ''); // Removing it to not show any content for now in tooltip will revert this once we have this implementation from backend
						return <>
							<span dangerouslySetInnerHTML={{ "__html": split[0]! }} />

							<DataMaskingComponent hooversFreeTrialDirectoryPathRewritten={hooversFreeTrialDirectoryPathRewritten} />

							<span dangerouslySetInnerHTML={{ "__html": modifiedSplit1! }} />
						</>;
					})()
					:
					(
						creditReportsDataMasked
							?
							(() => {
								const split = description!.replace(`?${creditReportsSentinelValue}`, creditReportsSentinelValue).split(creditReportsSentinelValue);
								return <>
									<span dangerouslySetInnerHTML={{ "__html": split[0]! }} />

									<DataMaskingCreditReportsComponent buyCreditReportsDirectoryPathRewritten={buyCreditReportsDirectoryPathRewritten} />

									<span dangerouslySetInnerHTML={{ "__html": split[1]! }} />
								</>;
							})()
							: <div dangerouslySetInnerHTML={{ "__html": description!.replace(/data-tippy-content="tippyContent"/g, '') }} />
					)
			}
		</div>
	</div>;
};

type BusinessDirectoryAccordionFragmentComponentProps = BusinessDirectoryAccordionFragmentParameter;

const BusinessDirectoryAccordion = ({ faqsData }: BusinessDirectoryAccordionFragmentComponentProps): JSX.Element => {
	const {
		accordionEntriesOne,
		accordionEntriesTwo,
		buyCreditReportsDirectoryPathRewritten,
		hooversFreeTrialDirectoryPathRewritten,
		linkPath,
		linkText
	} = faqsData;
  const {t} = useTranslation();
	useEffect(() => {
		try {
			window["___SET_ANCHORS_SECTION_DATA___"]();
		} catch (er) {
			dnbComLog(LogSeverity.ERROR, er);
		}
	}, []);

	const renderColumn = (isFirstColumn: boolean) => {
		const accordionEntries = isFirstColumn
			? accordionEntriesOne
			: accordionEntriesTwo;

		return <div
			className={`col-md-6 businessDirectoryAccordionColumn isFirstColumn-${isFirstColumn}`}
		>
			{
				accordionEntries.map((ac, nu) => {
					const {
						accordionDescription,
						accordionTitle
					} = ac;

					const accordionLabel = `accordion_${nu}_id`;
					const accordionId = `accordion_section_${nu}`;

					return <AccordionListComponent
						buyCreditReportsDirectoryPathRewritten={buyCreditReportsDirectoryPathRewritten}
						description={accordionDescription}
						hooversFreeTrialDirectoryPathRewritten={hooversFreeTrialDirectoryPathRewritten}
						identifyingClassName={accordionId}
						index={nu}
						key={nu}
						label={accordionLabel}
						title={accordionTitle} />;
				})
			}
		</div>;
	};

	return (
		<div className="business-directory-accordion">
			<a
				className="anchor"
				data-name="faq"
			/>
			<Paper
				title="Frequently asked questions"
				additionalContainerClass="row"
			>
				<div className="link-faq">
					<a
						href={t("companyProfile.contactCompanyProfileHref")}
						onClick={() => track_event(`${linkText} clicked`)}
					>
						{linkText}

						<span className="chevron-right-arrow-blue" />
					</a>
				</div>

				<div className="row">
					{
						renderColumn(true)
					}

					{
						renderColumn(false)
					}
				</div>
				<div className="link mobile-link">
					<a href={linkPath}>
						{linkText}

						<span className="chevron-right-arrow-blue" />
					</a>
				</div>
			</Paper>
		</div>
	);
};

export default BusinessDirectoryAccordion;
